import React, { useState, useRef } from 'react'
import { Loader2, CheckCircle2, XCircle, Network } from 'lucide-react'
import { PROTOCOL_COLORS } from '@/constants/activityTypes'

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

interface ShuffledQuestion {
  id?: number | string
  category?: string
  difficulty?: string
  question: string
  options: string[]
  explanation?: string
  hints?: string[]
  packets: any[]
  shuffledOptions: string[]
  shuffledCorrectAnswer: number
}

function shuffleQuestionOptions(q: Record<string, any>): ShuffledQuestion {
  const opts = q.options as string[]
  const indices = opts.map((_: string, i: number) => i)
  const shuffledIndices = shuffleArray(indices)
  const shuffledOptions = shuffledIndices.map((i: number) => opts[i])
  const shuffledCorrectAnswer = shuffledIndices.indexOf(q.correct_answer as number)
  return { ...q, packets: q.packets || [], shuffledOptions, shuffledCorrectAnswer } as ShuffledQuestion
}

interface PacketAnalysisActivityProps {
  activityData: Record<string, any>
  disabled?: boolean
  onSubmitQuiz: (data: Record<string, any>) => Promise<{ success: boolean; error?: string }>
  onComplete?: () => void | Promise<void>
}

export function PacketAnalysisActivity({ activityData, disabled = false, onSubmitQuiz, onComplete }: PacketAnalysisActivityProps) {
  const data = activityData || {}

  const [question] = useState<ShuffledQuestion | null>(() => {
    if (data.question && Array.isArray(data.options) && typeof data.correct_answer === 'number' && Array.isArray(data.packets)) {
      return shuffleQuestionOptions(data)
    }
    return null
  })

  const [selectedOption, setSelectedOption] = useState<number | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [expandedPacket, setExpandedPacket] = useState<number | null>(null)
  const [showHex, setShowHex] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const hasSubmittedLocally = useRef(false)

  const isCorrect = selectedOption !== null && selectedOption === question?.shuffledCorrectAnswer

  const handleChoice = (optionIndex: number) => {
    if (confirmed) return
    setSelectedOption(optionIndex)
  }

  const handleConfirm = async () => {
    if (selectedOption === null || confirmed || !question) return
    setConfirmed(true)
    hasSubmittedLocally.current = true

    setSubmitting(true)
    const answer = {
      question_id: question.id,
      choice: selectedOption,
      option_text: question.shuffledOptions[selectedOption],
      correct: selectedOption === question.shuffledCorrectAnswer,
      category: question.category,
    }
    try {
      await onSubmitQuiz({
        answers: [answer],
        total_correct: answer.correct ? 1 : 0,
        total_questions: 1,
        category_breakdown: {
          [question.category || 'Unknown']: { correct: answer.correct ? 1 : 0, total: 1 },
        },
      })
    } catch {
      // submission failed silently
    } finally {
      setSubmitting(false)
    }
  }

  const handleDismiss = () => {
    if (onComplete) {
      onComplete()
    } else {
      setSubmitted(true)
    }
  }

  if (submitted || (disabled && !hasSubmittedLocally.current)) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-subtle border border-theme-primary flex items-center justify-center">
          <svg className="w-8 h-8 text-theme-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-theme-primary mb-2">Waiting for Next Activity</h3>
        <p className="text-theme-text text-sm">Your captain will start the next activity soon.</p>
      </div>
    )
  }

  if (!question) return null

  const getProtocolColor = (protocol: string) => {
    return PROTOCOL_COLORS[protocol] || 'bg-gray-100 dark:bg-gray-900/40 text-gray-700 dark:text-gray-300'
  }

  const difficultyColor: Record<string, string> = {
    easy: 'text-[var(--color-success)] bg-[var(--color-success-subtle)]',
    medium: 'text-[var(--color-warning)] bg-[var(--color-warning-subtle)]',
    hard: 'text-[var(--color-danger)] bg-[var(--color-danger-subtle)]',
  }

  return (
    <div className="p-2 sm:p-4 space-y-4 overflow-hidden">
      {/* Wireshark-style packet viewer */}
      <div className="rounded-xl overflow-hidden border border-theme-border bg-theme-bg">
        {/* Title bar */}
        <div className="flex items-center gap-2 px-3 py-2 bg-surface-deep border-b border-theme-border">
          <Network className="w-4 h-4 text-[var(--color-primary)]" />
          <span className="text-xs text-theme-text font-medium">{question.packets.length} packets captured</span>
          <div className="flex-1" />
          {question.packets.some(p => p.hex) && (
            <button
              onClick={() => setShowHex(h => !h)}
              className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                showHex ? 'bg-[var(--color-primary)] text-[var(--color-on-primary)] border-[var(--color-primary)]' : 'text-theme-text-muted border-theme-border hover:border-theme-text-muted'
              }`}
            >
              HEX
            </button>
          )}
        </div>

        {/* Column headers */}
        <div className="overflow-x-auto scrollbar-thin">
          <div className="min-w-[600px]">
            <div className="grid grid-cols-[2.5rem_4rem_7rem_7rem_3rem_2.5rem_1fr] gap-1 px-2 py-1.5 text-[10px] text-theme-muted font-semibold font-mono border-b border-theme-border bg-theme-bg">
              <span>No</span><span>Time</span><span>Source</span><span>Destination</span><span>Proto</span><span>Len</span><span>Info</span>
            </div>

            {/* Packet rows */}
            <div className="max-h-64 overflow-y-auto scrollbar-thin">
              {question.packets.map((pkt) => (
                <div key={pkt.no}>
                  <div
                    onClick={() => pkt.layers ? setExpandedPacket(expandedPacket === pkt.no ? null : pkt.no) : undefined}
                    className={`grid grid-cols-[2.5rem_4rem_7rem_7rem_3rem_2.5rem_1fr] gap-1 px-2 py-1 text-[11px] font-mono transition-colors ${getProtocolColor(pkt.protocol)} ${
                      pkt.layers ? 'cursor-pointer hover:bg-surface-hover' : ''
                    } ${expandedPacket === pkt.no ? 'bg-surface-hover' : ''}`}
                  >
                    <span>{pkt.no}</span>
                    <span>{pkt.time.length > 8 ? pkt.time.slice(0, 8) : pkt.time}</span>
                    <span className="truncate">{pkt.source}</span>
                    <span className="truncate">{pkt.destination}</span>
                    <span className="font-bold">{pkt.protocol}</span>
                    <span>{pkt.length}</span>
                    <span className="truncate">{pkt.info}</span>
                  </div>

                  {/* Expanded layer detail */}
                  {expandedPacket === pkt.no && pkt.layers && (
                    <div className="bg-theme-bg border-t border-b border-theme-border px-3 py-2 space-y-2">
                      {pkt.layers.map((layer: any, li: number) => (
                        <div key={li}>
                          <div className="text-[10px] font-semibold text-[var(--color-primary)] mb-1">▸ {layer.name}</div>
                          <div className="pl-3 space-y-0.5">
                            {layer.fields.map((field: any, fi: number) => (
                              <div key={fi} className="flex gap-2 text-[10px] font-mono">
                                <span className="text-theme-muted shrink-0">{field.label}:</span>
                                <span className={field.highlight ? 'text-[var(--color-warning)] font-semibold' : 'text-theme-text'}>{field.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}

                      {/* Hex dump */}
                      {showHex && pkt.hex && (
                        <div className="mt-2 pt-2 border-t border-theme-border">
                          <div className="text-[10px] font-semibold text-theme-muted mb-1">Hex Dump</div>
                          <pre className="text-[10px] text-theme-text-muted font-mono whitespace-pre-wrap break-all">{pkt.hex}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Question + MCQ options */}
      <div className="rounded-xl overflow-hidden">
        {/* Category + difficulty header */}
        {(question.category || question.difficulty) && (
          <div className="flex items-center gap-2 px-4 py-2.5">
            {question.category && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--color-primary)]/20 text-[var(--color-primary)]">
                <Network className="w-3.5 h-3.5" />
                {question.category}
              </span>
            )}
            {question.difficulty && (
              <span className={`ml-auto px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase ${difficultyColor[question.difficulty] || 'text-theme-text-muted'}`}>
                {question.difficulty}
              </span>
            )}
          </div>
        )}

        {/* Question */}
        <div className="px-4 pb-4 pt-1">
          <p className="text-sm font-semibold text-green-500 leading-relaxed whitespace-pre-wrap">{question.question}</p>
        </div>
        <div className="space-y-2">
          {question.shuffledOptions.map((opt, idx) => {
            const isSelected = selectedOption === idx
            const isAnswer = question.shuffledCorrectAnswer === idx

            let btnClass = 'border-[var(--color-border)] bg-[var(--color-surface)] text-theme-text hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)]/10'
            if (confirmed) {
              if (isAnswer) {
                btnClass = 'border-[var(--color-success)] bg-[var(--color-success-subtle)] text-[var(--color-success)]'
              } else if (isSelected && !isAnswer) {
                btnClass = 'border-[var(--color-danger)] bg-[var(--color-danger-subtle)] text-[var(--color-danger)]'
              } else {
                btnClass = 'border-[var(--color-border)] bg-[var(--color-surface)] text-theme-muted'
              }
            } else if (isSelected) {
              btnClass = 'border-[var(--color-primary)] bg-[var(--color-primary)]/20 text-theme-text'
            }

            return (
              <button
                key={idx}
                onClick={() => handleChoice(idx)}
                disabled={confirmed}
                className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-all ${btnClass} disabled:cursor-default`}
              >
                <span className="mr-2 text-theme-muted font-mono text-xs">{String.fromCharCode(65 + idx)}.</span>
                {opt}
              </button>
            )
          })}
        </div>
      </div>

      {/* Submit button (before reveal) */}
      {selectedOption !== null && !confirmed && (
        <button
          onClick={handleConfirm}
          disabled={submitting}
          className="w-full py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 text-[var(--color-on-primary)]"
          style={{ background: 'var(--gradient-primary)' }}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting…
            </span>
          ) : 'Submit Answer'}
        </button>
      )}

      {/* Feedback panel (after submit) */}
      {confirmed && (
        <div className="rounded-xl overflow-hidden border border-theme-border">
          <div className={`flex items-center gap-3 px-4 py-3 ${isCorrect ? 'bg-[var(--color-success-subtle)] border-b border-[var(--color-success)]/30' : 'bg-[var(--color-danger-subtle)] border-b border-[var(--color-danger)]/30'}`}>
            {isCorrect
              ? <CheckCircle2 className="w-5 h-5 text-[var(--color-success)] shrink-0" />
              : <XCircle className="w-5 h-5 text-[var(--color-danger)] shrink-0" />
            }
            <span className={`flex-1 min-w-0 break-words font-semibold text-sm ${isCorrect ? 'text-[var(--color-success)]' : 'text-[var(--color-danger)]'}`}>
              {isCorrect ? 'Correct!' : `Incorrect — the answer is: ${question.shuffledOptions[question.shuffledCorrectAnswer]}`}
            </span>
          </div>

          {/* Explanation */}
          <div className="bg-surface px-4 py-3 space-y-2">
            <p className="text-xs text-theme-text leading-relaxed">{question.explanation || 'No explanation provided.'}</p>
          </div>

          {/* Clues */}
          {question.hints && question.hints.length > 0 && (
            <div className="bg-surface px-4 py-3 space-y-2 border-t border-theme-border">
              <p className="text-xs font-semibold text-theme-text-muted uppercase tracking-wide">Key indicators:</p>
              <ul className="space-y-2">
                {question.hints.map((clue, i) => (
                  <li key={i} className="flex gap-2 text-xs text-theme-text leading-snug">
                    <span className="text-[var(--color-secondary)] font-bold shrink-0 mt-0.5">→</span>
                    <span>{clue}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Advance button — self-paced only; in live mode the host controls progression */}
          {onComplete && (
            <div className="px-4 py-3 bg-surface border-t border-theme-border">
              <button
                onClick={handleDismiss}
                disabled={submitting}
                className="w-full py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-[var(--color-on-primary)]"
                style={{ background: 'var(--gradient-primary)' }}
              >
                Start next activity →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
